import * as THREE from 'three';
import { Camera } from '../rendering/Camera';
import { FrustumCullingSystem } from './FrustumCullingSystem';
import { OcclusionCullingSystem } from './OcclusionCullingSystem';
import { ObjectManager } from '../objects/ObjectManager';
import { GameObject } from '../objects/GameObject';
import { SpatialManager } from './SpatialManager';
import { Config } from '../core/Config';

/**
 * Manages culling operations and integrates with the main game systems
 * Provides a central point for all culling operations and visualization toggles
 */
export class CullingManager {
  private static instance: CullingManager | null = null;
  
  private camera: Camera;
  private scene: THREE.Scene;
  private renderer: THREE.WebGLRenderer;
  private objectManager: ObjectManager;
  
  private frustumCulling: FrustumCullingSystem;
  private occlusionCulling: OcclusionCullingSystem;
  
  // Visible objects after culling
  private visibleObjects: GameObject[] = [];
  
  // Performance stats
  private frameTime: number = 0;
  private objectsCulled: number = 0;
  
  // Configuration
  private frustumCullingEnabled: boolean = true;
  private occlusionCullingEnabled: boolean = true;
  private debugMode: boolean = false;
  
  // LOD update batching
  private lodUpdateInterval: number = 10; // Update LOD every N frames
  private currentFrame: number = 0;
  
  private constructor(
    camera: Camera, 
    scene: THREE.Scene, 
    renderer: THREE.WebGLRenderer,
    objectManager: ObjectManager
  ) {
    this.camera = camera;
    this.scene = scene;
    this.renderer = renderer;
    this.objectManager = objectManager;
    
    // Initialize culling systems
    this.frustumCulling = FrustumCullingSystem.getInstance(camera, scene);
    this.occlusionCulling = OcclusionCullingSystem.getInstance(camera, scene, renderer);
    
    // Get the spatial manager from the object manager and set it on the occlusion system
    const spatialManager = (this.objectManager as any)['spatialManager'];
    if (spatialManager) {
      this.occlusionCulling.setSpatialManager(spatialManager);
    }
    
    console.log('CullingManager initialized');
  }
  
  /**
   * Get the singleton instance
   */
  public static getInstance(
    camera?: Camera, 
    scene?: THREE.Scene, 
    renderer?: THREE.WebGLRenderer,
    objectManager?: ObjectManager
  ): CullingManager {
    if (!CullingManager.instance && camera && scene && renderer && objectManager) {
      CullingManager.instance = new CullingManager(camera, scene, renderer, objectManager);
    } else if (!CullingManager.instance && (!camera || !scene || !renderer || !objectManager)) {
      throw new Error('CullingManager not initialized. Provide camera, scene, renderer, and objectManager on first call.');
    }
    
    return CullingManager.instance as CullingManager;
  }
  
  /**
   * Update culling for the current frame
   * Returns the list of visible objects
   */
  public update(): GameObject[] {
    const startTime = performance.now();
    
    // Get all objects from the object manager
    const allObjects = this.objectManager.getAllObjects();
    
    // Apply culling based on enabled systems
    let visibleObjects: GameObject[] = allObjects;
    
    if (this.frustumCullingEnabled && this.occlusionCullingEnabled) {
      // If both are enabled, use occlusion culling which already includes frustum culling
      visibleObjects = this.occlusionCulling.cullObjects(allObjects);
    } else if (this.frustumCullingEnabled) {
      // Only use frustum culling - need to call differently based on whether we have a spatial manager
      const spatialManager = (this.objectManager as any)['spatialManager'];
      if (spatialManager) {
        const quadtree = spatialManager['quadtree'];
        if (quadtree) {
          visibleObjects = this.frustumCulling.cullQuadtree(quadtree);
        }
      }
    }
    
    // Update LOD for visible objects only
    this.updateLODForVisibleObjects(visibleObjects);
    
    // Update stats
    this.frameTime = performance.now() - startTime;
    this.objectsCulled = allObjects.length - visibleObjects.length;
    this.visibleObjects = visibleObjects;
    
    return visibleObjects;
  }
  
  /**
   * Update LOD for visible objects, batched for performance
   */
  private updateLODForVisibleObjects(visibleObjects: GameObject[]): void {
    // Only update LOD every N frames to improve performance
    this.currentFrame = (this.currentFrame + 1) % this.lodUpdateInterval;
    if (this.currentFrame !== 0) {
      return;
    }
    
    // For each visible object, update its LOD level
    const cameraPosition = this.camera.getPosition();
    
    // Batch process LOD updates by distance ranges
    const nearObjects: GameObject[] = [];
    const midObjects: GameObject[] = [];
    const farObjects: GameObject[] = [];
    
    const nearDistance = Config.LOD_HIGH_DISTANCE;
    const farDistance = Config.LOD_MEDIUM_DISTANCE;
    
    // Group objects by distance for batch processing
    for (const object of visibleObjects) {
      const position = object.getPosition();
      const distanceSquared = position.distanceToSquared(cameraPosition);
      
      if (distanceSquared < nearDistance * nearDistance) {
        nearObjects.push(object);
      } else if (distanceSquared < farDistance * farDistance) {
        midObjects.push(object);
      } else {
        farObjects.push(object);
      }
    }
    
    // Batch update each group with the corresponding LOD level
    this.batchUpdateLOD(nearObjects, 0); // High detail
    this.batchUpdateLOD(midObjects, 1);  // Medium detail
    this.batchUpdateLOD(farObjects, 2);  // Low detail
  }
  
  /**
   * Update LOD level for a batch of objects
   */
  private batchUpdateLOD(objects: GameObject[], lodLevel: number): void {
    for (const object of objects) {
      object.setLODLevel(lodLevel);
    }
  }
  
  /**
   * Enable or disable frustum culling
   */
  public setFrustumCullingEnabled(enabled: boolean): void {
    this.frustumCullingEnabled = enabled;
  }
  
  /**
   * Enable or disable occlusion culling
   */
  public setOcclusionCullingEnabled(enabled: boolean): void {
    this.occlusionCullingEnabled = enabled;
    this.occlusionCulling.setEnabled(enabled);
  }
  
  /**
   * Set the LOD update interval (in frames)
   */
  public setLODUpdateInterval(interval: number): void {
    this.lodUpdateInterval = Math.max(1, interval);
  }
  
  /**
   * Enable or disable debug visualization for all culling systems
   */
  public setDebugMode(enabled: boolean): void {
    // First, clean up any existing debug visualizations to prevent memory leaks
    this.cleanupDebugVisualizations();
    
    this.debugMode = enabled;
    this.frustumCulling.setDebugMode(enabled);
    this.occlusionCulling.setDebugMode(enabled);
  }
  
  /**
   * Toggle debug visualization
   */
  public toggleDebugMode(): void {
    this.setDebugMode(!this.debugMode);
  }
  
  /**
   * Clean up all debug visualizations from culling systems
   * Call this before enabling or disabling debug mode to prevent memory leaks
   */
  private cleanupDebugVisualizations(): void {
    // The FrustumCullingSystem provides a clearDebugObjects method
    if (this.frustumCulling) {
      this.frustumCulling.clearDebugObjects();
    }
    
    // Now that we've fixed the OcclusionCullingSystem, we can use clearDebugObjects directly
    if (this.occlusionCulling) {
      this.occlusionCulling.clearDebugObjects();
    }
    
    // Also clean up spatial manager debug visualizations if available
    const spatialManager = this.objectManager.getSpatialManager();
    if (spatialManager) {
      spatialManager.cleanupDebugVisualizations();
    }
  }
  
  /**
   * Get performance statistics
   */
  public getStats(): { 
    frameTime: number, 
    visibleObjects: number, 
    objectsCulled: number, 
    frustumStats: any, 
    occlusionStats: any 
  } {
    return {
      frameTime: this.frameTime,
      visibleObjects: this.visibleObjects.length,
      objectsCulled: this.objectsCulled,
      frustumStats: this.frustumCulling.getStats(),
      occlusionStats: this.occlusionCulling.getStats()
    };
  }
  
  /**
   * Get the frustum culling system
   */
  public getFrustumCullingSystem(): FrustumCullingSystem {
    return this.frustumCulling;
  }
  
  /**
   * Get a reference to the occlusion culling system
   */
  public getOcclusionCullingSystem(): OcclusionCullingSystem {
    return this.occlusionCulling;
  }
} 